import { sanitizeHiddenColumnKeys } from './columnVisibilityPreferences';

interface KanbanViewPreferenceHydrationParams {
  boardPreferencesLoaded: boolean;
  isStructureReady: boolean;
  allowedColumnKeys: readonly string[];
  hiddenColumnKeys: readonly string[] | undefined;
  visibleColumns: ReadonlySet<string>;
  /** Skip column-based hydration check for views that don't use Kanban columns (e.g., resource_schedule timeline) */
  skipColumnHydration?: boolean;
}

function areStringSetsEqual(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
}

export function getVisibleColumnKeysForKanbanView(
  hiddenColumnKeys: readonly string[] | undefined,
  allowedColumnKeys: readonly string[],
): string[] {
  if (allowedColumnKeys.length === 0) {
    return [];
  }

  if (!Array.isArray(hiddenColumnKeys)) {
    return [...allowedColumnKeys];
  }

  const hiddenColumnKeySet = new Set(
    sanitizeHiddenColumnKeys(hiddenColumnKeys, allowedColumnKeys),
  );

  return allowedColumnKeys.filter((columnKey) => !hiddenColumnKeySet.has(columnKey));
}

export function getVisibleColumnSetForKanbanView(
  hiddenColumnKeys: readonly string[] | undefined,
  allowedColumnKeys: readonly string[],
): Set<string> {
  return new Set(getVisibleColumnKeysForKanbanView(hiddenColumnKeys, allowedColumnKeys));
}

export function getKanbanHiddenResultsNotice(hiddenColumnCount: number): string | null {
  if (hiddenColumnCount <= 0) {
    return null;
  }

  return hiddenColumnCount === 1
    ? 'Hidden results due to preferences · 1 hidden column'
    : `Hidden results due to preferences · ${hiddenColumnCount} hidden columns`;
}

export function areKanbanViewPreferencesHydrated({
  boardPreferencesLoaded,
  isStructureReady,
  allowedColumnKeys,
  hiddenColumnKeys,
  visibleColumns,
  skipColumnHydration,
}: KanbanViewPreferenceHydrationParams): boolean {
  if (!boardPreferencesLoaded || !isStructureReady) {
    return false;
  }

  // Skip column-based hydration for views that don't use Kanban columns
  // (e.g., resource_schedule timeline renders resources directly, not via columns)
  // The timeline will render with empty state until resources load asynchronously
  if (skipColumnHydration) {
    return true;
  }

  if (allowedColumnKeys.length === 0) {
    return visibleColumns.size === 0;
  }

  return areStringSetsEqual(
    visibleColumns,
    getVisibleColumnSetForKanbanView(hiddenColumnKeys, allowedColumnKeys),
  );
}